import type { DomainTreeNodeDto } from '@terra/shared-contracts-assets';
import {
  DomainTreeNodeType,
  type DomainTree,
  type DomainTreeNode,
} from './domain-tree.utils';

function getDefaultExpanded(node: DomainTreeNodeDto): boolean {
  if (node.nodeType === DomainTreeNodeType.Domain) {
    return true;
  }

  if (node.nodeType === DomainTreeNodeType.Path) {
    const pathDepth = node.fullPath?.split('/').filter(Boolean).length ?? 0;

    return pathDepth <= 2;
  }

  return false;
}

function hydrateNode({
  dto,
  parent,
}: {
  dto: DomainTreeNodeDto;
  parent?: DomainTreeNode;
}): DomainTreeNode {
  const node: DomainTreeNode = {
    id: dto.id,
    name: dto.name,
    fullDomain: dto.fullDomain,
    level: dto.level,
    nodeType: dto.nodeType as DomainTreeNodeType,
    children: [],
    parent,
    assets: [],
    directEndpointIds: dto.directEndpointIds,
    directWebPageIds: dto.directWebPageIds,
    isExpanded: getDefaultExpanded(dto),
    assetCount: dto.assetCount,
    fullPath: dto.fullPath,
    method: dto.method,
    assetId: dto.assetId,
    isArchived: dto.isArchived,
  };

  node.children = dto.children.map((child) =>
    hydrateNode({ dto: child, parent: node }),
  );

  return node;
}

export function cloneDomainTree(tree: DomainTree): DomainTree {
  const nodeMap = new Map<string, DomainTreeNode>();

  const cloneNode = (
    node: DomainTreeNode,
    parent?: DomainTreeNode,
  ): DomainTreeNode => {
    const clonedNode: DomainTreeNode = {
      ...node,
      assets: [...node.assets],
      directEndpointIds: [...node.directEndpointIds],
      directWebPageIds: [...node.directWebPageIds],
      children: [],
      parent,
    };

    nodeMap.set(clonedNode.id, clonedNode);
    clonedNode.children = node.children.map((child) =>
      cloneNode(child, clonedNode),
    );

    return clonedNode;
  };

  const roots = tree.roots.map((root) => cloneNode(root));

  return { roots, nodeMap };
}

export function hydrateDomainTreeFromDto(
  roots: DomainTreeNodeDto[],
): DomainTree {
  const nodeMap = new Map<string, DomainTreeNode>();
  const hydratedRoots = roots.map((root) => {
    const hydratedRoot = hydrateNode({ dto: root });
    const registerNodes = (node: DomainTreeNode) => {
      nodeMap.set(node.id, node);
      node.children.forEach(registerNodes);
    };

    registerNodes(hydratedRoot);

    return hydratedRoot;
  });

  return {
    roots: hydratedRoots,
    nodeMap,
  };
}
